import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import {
  FundDetailActions,
  FundDetailPanel,
  FundDetailProvider,
  type FundDetail,
} from '@/components/giving/fund-detail-panel';
import { CurrencySelect } from '@/components/shared/currency-select';
import { FormField } from '@/components/shared/form-field';
import {
  decodeFundTypeValue,
  encodeFundTypeValue,
  FundTypeSelect,
  fundTypeLabel,
} from '@/components/shared/fund-type-select';
import { PageHeader } from '@/components/shared/page-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SwitchField } from '@/components/ui/switch';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { hasPermission, PERMISSIONS } from '@/lib/permissions';
import { useChurchStore } from '@/stores/church-store';

import { GivingSubnav } from './giving-subnav';

export const FundDetailPage = () => {
  const { fundId = '' } = useParams();
  const { t } = useTranslation('giving');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const church = useChurchStore((state) => state.church);
  const membership = useChurchStore((state) => state.membership);

  const canManage = hasPermission(membership, PERMISSIONS.GIVING_MANAGE_FUNDS);

  const [editOpen, setEditOpen] = useState(false);
  const [name, setName] = useState('');
  const [typeValue, setTypeValue] = useState('offering');
  const [defaultCurrency, setDefaultCurrency] = useState(
    () => church?.baseCurrency ?? 'GHS',
  );
  const [hideIdentity, setHideIdentity] = useState(false);
  const [acceptsPledges, setAcceptsPledges] = useState(true);
  const [availableForPrograms, setAvailableForPrograms] = useState(false);
  const [isActive, setIsActive] = useState(true);

  const fundCurrencies = useMemo(() => {
    const preferred = ['GHS', 'USD', 'EUR', 'GBP', 'NGN', 'KES', 'ZAR'];
    const fromChurch = [
      church?.baseCurrency,
      ...(church?.supportedCurrencies ?? []),
    ].filter((code): code is string => Boolean(code));
    return [...new Set([...fromChurch, ...preferred])];
  }, [church?.baseCurrency, church?.supportedCurrencies]);

  const detail = useQuery({
    queryKey: ['giving', 'funds', fundId],
    queryFn: () => api.get<FundDetail>(`/giving/funds/${fundId}`),
    enabled: Boolean(fundId),
  });

  const fund = detail.data;

  useEffect(() => {
    if (!editOpen || !fund) return;
    setName(fund.name);
    setTypeValue(encodeFundTypeValue(fund.fundType, fund.customTypeId));
    setDefaultCurrency(fund.defaultCurrency);
    setHideIdentity(!fund.recordGiverIdentity);
    setAcceptsPledges(fund.acceptsPledges);
    setAvailableForPrograms(fund.availableForPrograms);
    setIsActive(fund.isActive);
  }, [editOpen, fund]);

  const save = useMutation({
    mutationFn: () => {
      const { fundType, customTypeId } = decodeFundTypeValue(typeValue);
      return api.patch<FundDetail>(`/giving/funds/${fundId}`, {
        name,
        fundType,
        customTypeId,
        defaultCurrency,
        recordGiverIdentity: !hideIdentity,
        acceptsPledges,
        availableForPrograms,
        isActive,
      });
    },
    onSuccess: () => {
      toast.success(t('funds.saved'));
      setEditOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['giving', 'funds', fundId] });
      void queryClient.invalidateQueries({ queryKey: ['giving', 'funds'] });
      void queryClient.invalidateQueries({ queryKey: ['giving', 'funds', 'active'] });
      void queryClient.invalidateQueries({ queryKey: ['giving', 'funds', 'insights'] });
      void queryClient.invalidateQueries({ queryKey: ['programs', 'registration-funds'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  if (detail.isLoading) {
    return (
      <div>
        <PageHeader
          title={t('funds.detail.title')}
          breadcrumbs={[
            { label: t('funds.title'), to: '/giving/funds' },
            { label: tCommon('state.loading') },
          ]}
        />
        <GivingSubnav />
        <p className="text-body-sm text-fg-secondary">{tCommon('state.loading')}</p>
      </div>
    );
  }

  if (!fund) {
    return (
      <div>
        <PageHeader
          title={t('funds.detail.title')}
          breadcrumbs={[
            { label: t('funds.title'), to: '/giving/funds' },
            { label: tCommon('state.notFoundTitle') },
          ]}
        />
        <GivingSubnav />
        <p className="text-body-sm text-fg-secondary">{tCommon('state.notFoundBody')}</p>
      </div>
    );
  }

  return (
    <div>
      <FundDetailProvider fund={fund}>
        <PageHeader
          title={fund.name}
          breadcrumbs={[
            { label: t('funds.title'), to: '/giving/funds' },
            { label: fund.name },
          ]}
          meta={
            <>
              <Badge variant={fund.isActive ? 'success' : 'neutral'}>
                {fund.isActive ? t('funds.statusActive') : t('funds.inactive')}
              </Badge>
              <span className="text-body-sm text-fg-secondary">
                {t('funds.detail.description', {
                  code: fund.code,
                  type: fundTypeLabel(fund, t),
                })}
              </span>
            </>
          }
          headerActions={
            <div className="flex flex-wrap justify-end gap-2">
              {canManage ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setEditOpen(true)}
                >
                  {t('funds.edit')}
                </Button>
              ) : null}
              <FundDetailActions />
            </div>
          }
        />
        <GivingSubnav />
        <FundDetailPanel fund={fund} />
      </FundDetailProvider>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent closeLabel={tCommon('action.close')} className="overflow-visible">
          <DialogHeader>
            <DialogTitle>{t('funds.edit')}</DialogTitle>
          </DialogHeader>
          <form
            className="flex flex-col gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              if (!name.trim()) return;
              save.mutate();
            }}
          >
            <div className="flex flex-col gap-1.5">
              <Label>{t('funds.name')}</Label>
              <Input value={name} onChange={(event) => setName(event.target.value)} required />
            </div>
            <FormField label={t('funds.code')} hint={t('funds.codeLocked')}>
              {({ id, ...field }) => (
                <Input id={id} {...field} value={fund.code} disabled />
              )}
            </FormField>
            <div className="flex flex-col gap-1.5">
              <Label>{t('funds.type')}</Label>
              <FundTypeSelect
                value={typeValue}
                onChange={setTypeValue}
                popoverPortal="dialog"
              />
            </div>
            <FormField
              label={t('funds.currency')}
              tipId="giving.funds.currency"
              popoverPortal="dialog"
            >
              {({ id }) => (
                <CurrencySelect
                  id={id}
                  value={defaultCurrency}
                  onChange={(next) =>
                    setDefaultCurrency(next ?? church?.baseCurrency ?? 'GHS')
                  }
                  currencies={fundCurrencies}
                  popoverPortal="dialog"
                />
              )}
            </FormField>
            <SwitchField
              id="fund-detail-privacy"
              label={t('funds.privacy')}
              tipId="giving.funds.privacy"
              checked={hideIdentity}
              onCheckedChange={setHideIdentity}
            />
            <SwitchField
              id="fund-detail-accepts-pledges"
              label={t('funds.acceptsPledges')}
              tipId="giving.funds.acceptsPledges"
              checked={acceptsPledges}
              onCheckedChange={setAcceptsPledges}
            />
            <SwitchField
              id="fund-detail-available-for-programs"
              label={t('funds.availableForPrograms')}
              tipId="giving.funds.availableForPrograms"
              checked={availableForPrograms}
              onCheckedChange={setAvailableForPrograms}
            />
            <SwitchField
              id="fund-detail-active"
              label={t('funds.active')}
              tipId="giving.funds.active"
              checked={isActive}
              onCheckedChange={setIsActive}
            />
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>
                {tCommon('action.cancel')}
              </Button>
              <Button type="submit" loading={save.isPending} disabled={!name.trim()}>
                {t('funds.save')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
